// Copyright (c) 2024 by Audere

import {
  Avatar,
  Box,
  Link,
  ListItemAvatar,
  Stack,
  Tooltip,
  Typography,
  capitalize,
} from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Error,
  Person as PersonIcon,
} from "@mui/icons-material";
import { MessageData, MessageRole } from "../features/api/labelApiSlice";
import React, { MouseEvent, useState } from "react";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { createStyleSheet } from "../utils/styles";
import { format } from "date-fns";
import { LabeledContent } from "../features/label/labelTypes";
import { getMessageText, isTranslatedMessageTask } from "../utils/taskUtils";

const Message: React.FC<{
  message: MessageData;
  taskContent?: LabeledContent;
}> = ({ message, taskContent }) => {
  const [shouldShowMessage, setShouldShowMessage] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const getMessageIcon = (role: MessageRole) =>
    role === MessageRole.user ? <PersonIcon /> : <WhatsAppIcon />;

  const roleColor =
    message.role === MessageRole.user ? "primary.light" : "success.light";

  const textMessage =
    taskContent && isTranslatedMessageTask(taskContent)
      ? getMessageText(message)
      : (message.translation?.internal_text ?? message.text);

  const copyMessageIcon = () => {
    return isSuccess ? (
      <CheckCircle
        fontSize="medium"
        color="success"
        data-testid="CheckCircleIcon"
      />
    ) : (
      <Error fontSize="medium" color="error" data-testid="ErrorIcon" />
    );
  };

  const onCopyText = (event: MouseEvent) => {
    const setMessageTimeout = () => {
      setShouldShowMessage(true);
      setTimeout(() => {
        setShouldShowMessage(false);
      }, 2000);
    };

    event.stopPropagation();
    navigator.clipboard
      .writeText(textMessage)
      .then(() => {
        setIsSuccess(true);
        setMessageTimeout();
      })
      .catch(() => {
        setIsSuccess(false);
        setMessageTimeout();
      });
  };

  return (
    <Box sx={styles.messageListItem}>
      <ListItemAvatar sx={{ alignContent: "center" }}>
        <Avatar sx={{ backgroundColor: roleColor }}>
          {getMessageIcon(message.role)}
        </Avatar>
      </ListItemAvatar>
      <Stack direction="column">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontWeight="bold">{capitalize(message.role)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {format(message.sent_ts, "p")}
          </Typography>
          <Tooltip title={"Copy message text"} placement="top">
            <Link onClick={onCopyText} sx={{ cursor: "pointer", fontSize: 15 }}>
              <ContentCopy fontSize="medium" />
            </Link>
          </Tooltip>
          {shouldShowMessage && copyMessageIcon()}
        </Stack>
        <Typography>{textMessage}</Typography>
      </Stack>
    </Box>
  );
};

const styles = createStyleSheet({
  messageListItem: {
    display: "flex",
    gap: 1,
    width: "100%",
  },
});

export default Message;
